import { useIsEmailOrPhone } from '@tino-frontend/form'

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  confirmPasswordReset,
  checkActionCode,
  signInWithCustomToken,
} from 'firebase/auth'

import { firebaseAuthClient } from 'lib/firebase/client'

import {
  ApiError,
  BaseApiService,
  type Credentials,
  type UserCredential,
  type ContactVerify,
  type ContactVerifyResponse,
} from 'api'

import { ApiRoutes } from 'constants/api'

import { AuthMappers } from './mappers'

export type SessionReturnType<T> = {
  token: string
  success: boolean
  data: T
}

export type VerifyOtpPayload = {
  phone: string
  otp: string
}

export type SignInVerifyOtpResponse = {
  custom_token: string
}

export class AuthService extends BaseApiService {
  constructor(
    private readonly mappers: AuthMappers,
    private readonly auth: string
  ) {
    super()
  }

  async onAuthStateChanged(): Promise<UserCredential> {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(firebaseAuthClient, (user) => {
        resolve(this.mappers.mapUser(user))
        unsubscribe()
      })
    })
  }

  async signIn({
    email,
    password
  }: Credentials): Promise<SessionReturnType<UserCredential>> {
    try {
      const userCredential = await signInWithEmailAndPassword(
        firebaseAuthClient,
        email,
        password
      )

      const token = await userCredential.user.getIdToken()

      return {
        token,
        success: true,
        data: this.mappers.mapUser(userCredential.user)
      }
    }
    catch (error) {
      throw new ApiError((error as Error).message, 500)
    }
  }

  async signInWithCustomToken(customToken: string): Promise<SessionReturnType<UserCredential>> {
    try {
      const userCredential = await signInWithCustomToken(firebaseAuthClient, customToken)

      return {
        token: customToken,
        success: true,
        data: this.mappers.mapUser(userCredential.user)
      }
    }
    catch (error) {
      throw new ApiError((error as Error).message, 500)
    }
  }

  async signUp({
    email,
    password
  }: Credentials): Promise<SessionReturnType<UserCredential>> {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        firebaseAuthClient,
        email,
        password
      )
      const token = await userCredential.user.getIdToken()

      return {
        token,
        success: true,
        data: this.mappers.mapUser(userCredential.user)
      }
    }
    catch (error) {
      throw new ApiError((error as Error).message, 500)
    }
  }

  async signOut(): Promise<void> {
    await firebaseSignOut(firebaseAuthClient)
  }

  async verifyContact(contact: string): Promise<ContactVerify> {
    const isEmail = useIsEmailOrPhone(contact) === 'email'

    const emailPath = `${this.auth}/v1/emails/${contact}/valid`
    const phonePath = `${this.auth}/v1/phones/+55${contact}/valid`
    const path = isEmail ? emailPath : phonePath

    const response = await this.request<ContactVerifyResponse>(path)
    return this.mappers.mapContact(response)
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    return await sendPasswordResetEmail(firebaseAuthClient, email)
  }

  async confirmPasswordReset(code: string, password: string): Promise<void> {
    return await confirmPasswordReset(firebaseAuthClient, code, password)
  }

  async checkRecoveryCode(code: string): Promise<{ email?: string | null }> {
    try {
      const actionCodeInfo = await checkActionCode(firebaseAuthClient, code)
      return {
        email: actionCodeInfo.data.email
      }
    }
    catch (error) {
      throw new ApiError((error as Error).message, 500)
    }
  }

  async verifyAuthenticatedPhone(): Promise<{ verified: boolean }> {
    const path = `${this.auth}/v1/phones/verified`
    return await this.request<{ verified: boolean }>(path)
  }

  async sendSignOtp(phone: string): Promise<void> {
    const path = `${this.auth}/v1/sign-in/whatsapp/otp`
    const body = JSON.stringify({ phone: `+55${phone}` })

    return await this.request<void>(path, {
      method: 'POST',
      body,
    })
  }

  async verifySignInOtp(payload: VerifyOtpPayload): Promise<SignInVerifyOtpResponse> {
    const path = `${this.auth}/v1/sign-in/whatsapp/otp/verify`

    const body = JSON.stringify(Object.assign(payload, {
      phone: `+55${payload.phone}`
    }))

    return await this.request<SignInVerifyOtpResponse>(path, {
      method: 'POST',
      body,
    })
  }
}

export const authService = new AuthService(
  new AuthMappers(),
  ApiRoutes.AUTH,
)
